// Button (009 revision 6, FR-044; shadcn/ui `button`, restyled on the spacing system): the one button of every app.
// Heights are the system's control heights: 44 px below 1024 px (touch) and 40 px (32 px `sm`) from 1024 px, or
// always 44 px with `size="touch"` (the reader app). `asChild` renders the styles on the child (a router link).
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/cn';

/** The button's classes by variant and size; other components (an alert dialog's actions) reuse them. */
export const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white hover:bg-primary/90',
        destructive: 'bg-danger text-white hover:bg-danger/90',
        outline: 'border border-line bg-surface text-ink hover:bg-subtle',
        secondary: 'bg-subtle text-ink hover:bg-line',
        ghost: 'text-ink hover:bg-subtle',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-control-touch px-4 lg:h-control',
        sm: 'h-control-touch px-3 lg:h-control-sm',
        lg: 'h-control-touch px-4',
        touch: 'h-control-touch px-4',
        icon: 'size-control-touch lg:size-control',
        'icon-sm': 'size-control-touch lg:size-control-sm',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

/** Props of {@link Button}: a button's attributes, the variant and size, and `asChild`. */
export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Render the styles on the only child (a link) instead of a `<button>`. */
    asChild?: boolean;
  };

/**
 * A button; `type` defaults to `button` so it never submits a form by accident.
 * @param props the variant, size, `asChild` and the button's attributes
 * @returns the button
 */
export function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  type,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      type={asChild ? type : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
